import React, { useEffect, useState } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  Image,
} from "@react-pdf/renderer";
import logo from "../../../../assets/img/marifer.png";
import { dataTransform } from "./functions";

const C = {
  pink: "#f43f5e",
  pinkLight: "#fce7f3",
  pinkMid: "#f9a8d4",
  pinkDark: "#be185d",
  gray: "#6b7280",
  grayLight: "#f3f4f6",
  dark: "#374151",
  white: "#ffffff",
};

const styles = StyleSheet.create({
  viewer: { width: "100%", height: window.innerHeight },
  page: { padding: 28, fontSize: 8, fontFamily: "Helvetica", backgroundColor: C.white },

  // ── Header ──
  header: {
    flexDirection: "row", alignItems: "center",
    borderBottom: `2px solid ${C.pink}`, paddingBottom: 10, marginBottom: 14,
  },
  logoImg: { width: 55, height: 55, marginRight: 12 },
  headerTitle: { fontSize: 16, fontFamily: "Helvetica-Bold", color: C.pink, marginBottom: 2 },
  headerSub: { fontSize: 8, color: C.gray },
  headerDate: { fontSize: 7, color: C.gray, marginTop: 1 },

  // ── Info bar ──
  infoBar: {
    flexDirection: "row", marginBottom: 14, gap: 6,
  },
  infoBox: {
    flex: 1, border: `1px solid ${C.pinkLight}`, borderRadius: 4,
    padding: 6, backgroundColor: "#fff0f6",
  },
  infoLabel: { fontSize: 6, color: C.pinkDark, fontFamily: "Helvetica-Bold", marginBottom: 2, textTransform: "uppercase" },
  infoValue: { fontSize: 9, color: C.dark, fontFamily: "Helvetica-Bold" },

  // ── Section header ──
  sectionHeader: {
    flexDirection: "row", backgroundColor: C.pink,
    borderRadius: 3, padding: "5px 8px", marginBottom: 2, marginTop: 10,
  },
  sectionHeaderText: { color: C.white, fontFamily: "Helvetica-Bold", fontSize: 8 },

  // ── Table ──
  tableRow: { flexDirection: "row", borderBottom: `1px solid ${C.pinkLight}`, padding: "4px 0" },
  tableRowAlt: { flexDirection: "row", borderBottom: `1px solid ${C.pinkLight}`, padding: "4px 0", backgroundColor: "#fff0f6" },
  tableHead: {
    flexDirection: "row", backgroundColor: C.pinkLight,
    padding: "4px 0", borderBottom: `1px solid ${C.pinkMid}`,
  },
  tableHeadText: { fontSize: 7, fontFamily: "Helvetica-Bold", color: C.pinkDark, paddingHorizontal: 4 },
  cellText: { fontSize: 7.5, color: C.dark, paddingHorizontal: 4 },
  cellBadge: {
    fontSize: 6.5, color: C.pinkDark, backgroundColor: C.pinkLight,
    paddingHorizontal: 4, paddingVertical: 1, borderRadius: 3,
  },

  // ── Sub-fila talla ──
  tallaRow: {
    flexDirection: "row", paddingVertical: 2, paddingHorizontal: 8,
    borderBottom: `1px solid ${C.pinkLight}`, backgroundColor: "#fdf2f8",
  },
  tallaRowAlt: {
    flexDirection: "row", paddingVertical: 2, paddingHorizontal: 8,
    borderBottom: `1px solid ${C.pinkLight}`, backgroundColor: C.white,
  },
  tallaCellText: { fontSize: 7, color: C.gray, paddingHorizontal: 4 },

  // ── Imágenes ──
  imgSection: { flexDirection: "row", flexWrap: "wrap", gap: 4, marginTop: 10 },
  imgItem: { width: 90, height: 90, borderRadius: 4, border: `1px solid ${C.pinkLight}` },

  // ── Footer ──
  footer: {
    flexDirection: "row", justifyContent: "space-between",
    marginTop: 14, borderTop: `1px solid ${C.pinkLight}`, paddingTop: 6,
  },
  footerText: { fontSize: 6.5, color: C.pinkMid },
});

const ProductSummaryPDF = ({ defaultDataDetail: d, producto }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const convert = async () => {
      const res = await dataTransform(d);
      setData(res);
    };
    convert();
  }, [d]);

  if (!data) return "Calculando...";

  // Separar insumos con y sin detalle por talla
  const insumosSimples = data.insumosGrouped.filter(i => !i.hasTalla);
  const insumosConTalla = data.insumosGrouped.filter(i => i.hasTalla);

  return (
    <PDFViewer style={styles.viewer}>
      <Document>
        <Page size="LETTER" style={styles.page}>

          {/* ── HEADER ── */}
          <View style={styles.header}>
            <Image style={styles.logoImg} src={logo} />
            <View>
              <Text style={styles.headerTitle}>Detalle del Producto</Text>
              <Text style={styles.headerSub}>Sistema de gestión Marifer</Text>
              <Text style={styles.headerDate}>
                Generado: {new Date().toLocaleDateString("es-MX")}
              </Text>
            </View>
          </View>

          {/* ── INFO BAR ── */}
          <View style={styles.infoBar}>
            {[
              { label: "Cliente", value: data.client },
              { label: "Nombre", value: data.name },
              { label: "Modelo", value: data.model },
              { label: "SKU", value: data.sku },
              { label: "Diseño", value: data.design },
              { label: "Estampado", value: data.estampado },
              { label: "Bordado", value: data.bordado },
            ].map((item, i) => (
              <View key={i} style={styles.infoBox}>
                <Text style={styles.infoLabel}>{item.label}</Text>
                <Text style={styles.infoValue}>{item.value || "—"}</Text>
              </View>
            ))}
          </View>

          {/* ── PRODUCTO: PRENDAS Y TALLAS ── */}
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>PRENDAS Y TALLAS</Text>
          </View>
          <View style={styles.tableHead}>
            <Text style={[styles.tableHeadText, { width: "40%" }]}>Prenda</Text>
            <Text style={[styles.tableHeadText, { width: "60%" }]}>Tallas del producto</Text>
          </View>
          {data.prenda.map((p, i) => (
            <View key={i} style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
              <Text style={[styles.cellText, { width: "40%" }]}>{p.nombre}</Text>
              <Text style={[styles.cellText, { width: "60%" }]}>
                {data.tallas.map(t => t.nombre).join("  ·  ")}
              </Text>
            </View>
          ))}

          {/* ── INSUMOS GENERALES ── */}
          {insumosSimples.length > 0 && (
            <>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>INSUMOS (cantidad general)</Text>
              </View>
              <View style={styles.tableHead}>
                <Text style={[styles.tableHeadText, { width: "50%" }]}>Nombre</Text>
                <Text style={[styles.tableHeadText, { width: "20%" }]}>Cantidad</Text>
                <Text style={[styles.tableHeadText, { width: "30%" }]}>Unidad</Text>
              </View>
              {insumosSimples.sort((a, b) => a.nombre.localeCompare(b.nombre)).map((item, i) => (
                <View key={i} style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
                  <Text style={[styles.cellText, { width: "50%" }]}>{item.nombre}</Text>
                  <Text style={[styles.cellText, { width: "20%" }]}>{Math.round(item.cantidad)}</Text>
                  <Text style={[styles.cellText, { width: "30%" }]}>{item.unidad || "—"}</Text>
                </View>
              ))}
            </>
          )}

          {/* ── INSUMOS POR TALLA ── */}
          {insumosConTalla.length > 0 && (
            <>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>INSUMOS ESPECIFICADOS POR TALLA</Text>
              </View>
              {insumosConTalla.map((item, i) => (
                <View key={i}>
                  {/* Nombre del insumo */}
                  <View style={{
                    flexDirection: "row", alignItems: "center",
                    backgroundColor: C.pinkLight, padding: "4px 8px",
                    borderBottom: `1px solid ${C.pinkMid}`,
                    marginTop: i > 0 ? 4 : 0,
                  }}>
                    <Text style={{ fontSize: 8, fontFamily: "Helvetica-Bold", color: C.pinkDark, flex: 1 }}>
                      {item.nombre}
                    </Text>
                    <Text style={{ fontSize: 7, color: C.gray }}>
                      {item.detallesPorTalla?.length || 0} tallas
                    </Text>
                  </View>
                  {/* Encabezado sub-tabla */}
                  <View style={{ flexDirection: "row", backgroundColor: "#fdf2f8", padding: "3px 8px", borderBottom: `1px solid ${C.pinkLight}` }}>
                    <Text style={[styles.tableHeadText, { width: "40%", paddingHorizontal: 4 }]}>Talla</Text>
                    <Text style={[styles.tableHeadText, { width: "30%", paddingHorizontal: 4 }]}>Cantidad</Text>
                    <Text style={[styles.tableHeadText, { width: "30%", paddingHorizontal: 4 }]}>Unidad</Text>
                  </View>
                  {/* Filas por talla */}
                  {(item.detallesPorTalla || [])
                    .sort((a, b) => (a.talla || "").localeCompare(b.talla || ""))
                    .map((d, j) => (
                      <View key={j} style={j % 2 === 0 ? styles.tallaRowAlt : styles.tallaRow}>
                        <Text style={[styles.tallaCellText, { width: "40%" }]}>{d.talla || "—"}</Text>
                        <Text style={[styles.tallaCellText, { width: "30%" }]}>{d.cantidad}</Text>
                        <Text style={[styles.tallaCellText, { width: "30%" }]}>{d.unidad || "—"}</Text>
                      </View>
                    ))}
                </View>
              ))}
            </>
          )}

          {/* ── CONSUMO DE TELA ── */}
          {data.clothConsumption.length > 0 && (
            <>
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionHeaderText}>CONSUMO DE TELA POR PRENDA</Text>
              </View>
              <View style={styles.tableHead}>
                <Text style={[styles.tableHeadText, { width: "45%" }]}>Tela</Text>
                <Text style={[styles.tableHeadText, { width: "30%" }]}>Prenda</Text>
                <Text style={[styles.tableHeadText, { width: "25%" }]}>Cantidad promedio</Text>
              </View>
              {data.clothConsumption.map((m, i) => (
                <View key={i} style={i % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
                  <Text style={[styles.cellText, { width: "45%" }]}>
                    {`(${m.cloth.code}) ${m.cloth.name}, ${m.cloth.color}, ${m.cloth.tone}`}
                  </Text>
                  <Text style={[styles.cellText, { width: "30%" }]}>{m.prenda.nombre}</Text>
                  <Text style={[styles.cellText, { width: "25%" }]}>{`${m.quantity} ${m.unit}`}</Text>
                </View>
              ))}
            </>
          )}

          {/* ── IMÁGENES ── */}
          {data.images.filter(Boolean).length > 0 && (
            <>
              <View style={[styles.sectionHeader, { marginTop: 12 }]}>
                <Text style={styles.sectionHeaderText}>IMÁGENES</Text>
              </View>
              <View style={styles.imgSection}>
                {data.images.filter(Boolean).map((img, i) => (
                  <Image key={i} style={styles.imgItem} src={img} />
                ))}
              </View>
            </>
          )}

          {/* ── FOOTER ── */}
          <View style={styles.footer}>
            <Text style={styles.footerText}>©Marifer {new Date().getFullYear()}</Text>
            <Text style={styles.footerText}>
              {data.name} — {new Date().toLocaleDateString("es-MX")}
            </Text>
          </View>

        </Page>
      </Document>
    </PDFViewer>
  );
};
export default ProductSummaryPDF;
